import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { MoreHorizontal, VolumeX, UserX, Flag, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL as API } from '../config';
import { useAuth } from '../context/AuthContext';

export function UserActionsDropdown({ userId, userName, className, isRelated: isRelatedProp }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ is_related: isRelatedProp ?? false });
    const [fetchingStatus, setFetchingStatus] = useState(isRelatedProp === undefined);

    useEffect(() => {
        if (isRelatedProp === true) {
            setStatus({ is_related: true });
            setFetchingStatus(false);
            return;
        }

        const fetchRelationship = async () => {
            if (!user || !userId || user.id === userId) {
                setFetchingStatus(false);
                return;
            }
            try {
                const response = await axios.get(`${API}/users/${userId}/relationship`);
                setStatus(response.data);
            } catch (error) {
                console.error('Failed to fetch relationship status:', error);
            } finally {
                setFetchingStatus(false);
            }
        };

        fetchRelationship();
    }, [userId, user, isRelatedProp]);

    const handleAction = async (actionType, label) => {
        setLoading(true);
        try {
            if (actionType === 'block') {
                await axios.post(`${API}/user-actions/block/${userId}`);
            } else if (actionType === 'mute') {
                await axios.post(`${API}/user-actions/mute/${userId}`);
            }
            toast.success(`${label} ${userName} successfully`);
        } catch (error) {
            toast.error(`Failed to ${actionType} user`);
        } finally {
            setLoading(false);
        }
    };

    if (!user || user.id === userId) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className={className} disabled={loading || fetchingStatus}>
                    <MoreHorizontal className="w-5 h-5 text-zinc-400" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 text-zinc-300">
                {status.is_related && (
                    <>
                        <DropdownMenuItem 
                            className="flex items-center gap-2 cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800"
                            onClick={() => handleAction('mute', 'Muted')}
                        >
                            <VolumeX className="w-4 h-4" />
                            Mute {userName}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            className="flex items-center gap-2 cursor-pointer text-red-400 hover:bg-zinc-800 focus:bg-zinc-800"
                            onClick={() => handleAction('block', 'Blocked')}
                        >
                            <Ban className="w-4 h-4" />
                            Block {userName}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-zinc-800" />
                    </>
                )}
                <DropdownMenuItem className="flex items-center gap-2 cursor-pointer text-zinc-500 hover:bg-zinc-800 focus:bg-zinc-800">
                    <Flag className="w-4 h-4" />
                    Report Profile
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
